// Edit-with-Forge dialog for an Agent's role_prompt — trimmed mirror of
// the skill-side DesignerPane (app/javascript/components/domain/designer-pane.tsx).
// Drops scenarios, prompt-section diffs, rehearsal hooks, and side-tabs
// — only `propose_field_update` proposals are handled here, since the
// only field Forge can edit on an Agent today is `role_prompt`.
//
// Endpoints (mirror /skills/:id/* on /agents/:id/*):
//   POST   /agents/:id/ai_chat            — SSE stream (text + tool_proposal events)
//   GET    /agents/:id/designer_chat      — load persisted thread on open
//   PATCH  /agents/:id/apply_proposals    — apply accepted field-update proposals
//   DELETE /agents/:id/designer_chat      — clear thread + pending proposals
//
// Usage:
//   <AgentDesignerPane open={open} onClose={...} agentId={agent.id}
//                      currentRolePrompt={agent.rolePrompt}
//                      onApplied={(updated) => setAgent(updated)} />
//
// On Apply, the dialog stays open and visually marks each card "✓ Applied"
// — operators can keep iterating without re-opening.

import { useCallback, useEffect, useRef, useState } from "react"
import { Send, Sparkles, Eraser, Check, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { ChatThinkingIndicator } from "@/components/domain/chat-thinking-indicator"
import { consumeSSE } from "@/lib/sse"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"
import { cn } from "@/lib/cn"
import type { Agent } from "@/types/agent"
import type { ChatBubble } from "@/types/test_lab"

type Message = {
  id:        number
  role:      "user" | "assistant"
  content:   string
  createdAt: string
}

type Proposal = {
  id:       string
  kind:     string
  input:    Record<string, unknown>
  number?:  number
  applied?: boolean
}

type DesignerLogRow = {
  id:             number
  role:           string
  content:        string | null
  toolProposal:   { id?: string; kind?: string; input?: Record<string, unknown> } | null
  proposalNumber: number | null
  createdAt:      string
}

type LogPage = {
  messages:     DesignerLogRow[]
  hasMore:      boolean
  nextBeforeId: number | null
}

type Props = {
  open: boolean
  onClose: () => void
  agentId: number
  currentRolePrompt: string
  onApplied: (newAgent: Agent) => void
}

const PAGE_SIZE = 50

function csrfHeaders(): HeadersInit {
  const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
  return {
    "X-CSRF-Token":     token ?? "",
    "Content-Type":     "application/x-www-form-urlencoded",
    "Accept":           "application/json",
    "X-Requested-With": "XMLHttpRequest",
  }
}

function partitionRows(rows: DesignerLogRow[]): { turns: Message[]; queued: Proposal[] } {
  const turns: Message[]  = []
  const queued: Proposal[] = []
  for (const m of rows) {
    if (m.role === "user" || m.role === "assistant") {
      turns.push({
        id:        m.id,
        role:      m.role,
        content:   m.content ?? "",
        createdAt: m.createdAt,
      })
    } else if (m.role === "tool_proposal" && m.toolProposal) {
      // Only field-update proposals are meaningful for the agent
      // designer right now — silently drop anything else so a stray
      // skill-shaped proposal in the log doesn't render an empty card.
      const kind = m.toolProposal.kind ?? ""
      if (kind !== "propose_field_update") continue
      queued.push({
        id:     m.toolProposal.id ?? `prop-${m.id}`,
        kind,
        input:  m.toolProposal.input ?? {},
        number: m.proposalNumber ?? undefined,
      })
    }
  }
  return { turns, queued }
}

export function AgentDesignerPane({
  open,
  onClose,
  agentId,
  currentRolePrompt,
  onApplied,
}: Props) {
  const [messages, setMessages]     = useState<Message[]>([])
  const [draft, setDraft]           = useState("")
  const [streaming, setStreaming]   = useState(false)
  const [proposals, setProposals]   = useState<Proposal[]>([])
  const [applyError, setApplyError] = useState<string | null>(null)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const [loadedFor, setLoadedFor]   = useState<number | null>(null)
  const threadRef = useRef<HTMLDivElement | null>(null)

  // Replay the most-recent page of the persisted thread when the
  // dialog opens. Re-fetch if the agentId changes between opens.
  useEffect(() => {
    if (!open) return
    if (loadedFor === agentId) return
    let cancelled = false
    fetch(`/agents/${agentId}/designer_chat?limit=${PAGE_SIZE}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() as Promise<LogPage> })
      .then((data) => {
        if (cancelled) return
        const { turns, queued } = partitionRows(data.messages)
        setMessages(turns)
        setProposals(queued)
        setLoadedFor(agentId)
      })
      .catch(() => {
        if (cancelled) return
        // Empty thread on error — operator can still chat.
        setMessages([])
        setProposals([])
        setLoadedFor(agentId)
      })
    return () => { cancelled = true }
  }, [open, agentId, loadedFor])

  // Auto-scroll the thread on new content / streaming flips.
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" })
  }, [messages.length, streaming, proposals.length])

  const streamReply = useCallback(async (history: Message[]) => {
    setStreaming(true)
    await consumeSSE(
      `/agents/${agentId}/ai_chat`,
      {
        method: "POST",
        body:    new URLSearchParams({ messages: JSON.stringify(history) }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
      {
        onText: (delta) => {
          setMessages((cur) => {
            const next = [...cur]
            const last = next[next.length - 1]
            if (last && last.role === "assistant") {
              next[next.length - 1] = { ...last, content: last.content + delta }
            }
            return next
          })
        },
        onToolProposal: (raw) => {
          const p = raw as { id?: string; kind?: string; input?: Record<string, unknown>; number?: number }
          // Only field-update proposals are surfaced in this dialog.
          if ((p.kind ?? "") !== "propose_field_update") return
          setProposals((cur) => [
            ...cur,
            {
              id:     p.id ?? `prop-${Date.now()}-${cur.length}`,
              kind:   "propose_field_update",
              input:  p.input ?? {},
              number: p.number,
            },
          ])
        },
        onError: (msg) => {
          setMessages((cur) => [
            ...cur,
            { id: Date.now(), role: "assistant", content: `(error: ${msg})`, createdAt: new Date().toISOString() },
          ])
        },
        onEnd: () => setStreaming(false),
      },
    )
    setStreaming(false)
  }, [agentId])

  const sendText = useCallback(async (rawText: string) => {
    const text = rawText.trim()
    if (streaming || !text) return
    const now = new Date().toISOString()
    const newHistory: Message[] = [
      ...messages,
      { id: Date.now(),     role: "user",      content: text, createdAt: now },
      { id: Date.now() + 1, role: "assistant", content: "",   createdAt: now },
    ]
    setMessages(newHistory)
    await streamReply(newHistory.slice(0, -1))
  }, [messages, streaming, streamReply])

  const send = useCallback(async () => {
    if (!draft.trim()) return
    const text = draft
    setDraft("")
    await sendText(text)
  }, [draft, sendText])

  async function applyOne(prop: Proposal) {
    if (applyingId) return
    setApplyingId(prop.id)
    setApplyError(null)
    try {
      const res = await fetch(`/agents/${agentId}/apply_proposals`, {
        method:      "PATCH",
        headers:     csrfHeaders(),
        credentials: "same-origin",
        body:        new URLSearchParams({ proposals: JSON.stringify([prop]) }),
      })
      const data = await res.json().catch(() => ({} as Record<string, unknown>))
      if (!res.ok) {
        setApplyError((data.error as string | undefined) ?? `HTTP ${res.status}`)
        return
      }
      const updated = (data.agent as Agent | undefined)
      if (updated) onApplied(updated)
      setProposals((cur) => cur.map((p) => (p.id === prop.id ? { ...p, applied: true } : p)))
    } catch (e) {
      setApplyError((e as Error).message)
    } finally {
      setApplyingId(null)
    }
  }

  function rejectOne(id: string) {
    setProposals((cur) => cur.filter((p) => p.id !== id))
  }

  async function clearChat() {
    const hasState = messages.length > 0 || proposals.length > 0
    if (hasState && !confirm("Clear the entire chat with Forge, including any pending changes?")) return
    try {
      await fetch(`/agents/${agentId}/designer_chat`, {
        method:      "DELETE",
        headers:     csrfHeaders(),
        credentials: "same-origin",
      })
    } catch {/* clear locally anyway */}
    setMessages([])
    setProposals([])
    setApplyError(null)
  }

  return (
    <Dialog open={open} onClose={onClose} size="lg" className="w-[min(96vw,1100px)] max-w-none">
      <DialogHeader
        title="Edit with Forge — Role prompt"
        subtitle="Discuss any changes you want to make to this agent's role prompt. Forge will draft updates as accept / reject cards."
      />
      <DialogBody className="space-y-3">
        <div className="flex h-[min(70vh,640px)] flex-col gap-3 lg:flex-row">
          {/* Left: chat thread + composer */}
          <Card className="flex flex-1 min-w-0 flex-col p-0">
            <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.7} className="text-vip-goldSoft" />
                <div>
                  <h3 className="text-sm font-semibold">Design with Forge</h3>
                  <p className="text-[10px] text-app-subtle">
                    Ask for tone changes, scope tweaks, new guardrails — Forge will propose them on the right.
                  </p>
                </div>
              </div>
              <Button
                type="button" size="sm" variant="ghost"
                onClick={clearChat}
                disabled={streaming || (messages.length === 0 && proposals.length === 0)}
                title="Clear chat history + pending changes"
              >
                <Eraser size={12} strokeWidth={2} className="mr-1" />
                Clear thread
              </Button>
            </header>

            <div ref={threadRef} className="flex-1 space-y-6 overflow-y-auto px-6 py-6 text-sm">
              {messages.length === 0 && (
                <p className="text-xs text-app-muted">
                  Tell Forge what to change — "make this terser", "add a guardrail about refunds", "lean warmer". Proposals land on the right.
                </p>
              )}
              {messages.map((m, i) => {
                const isStreamingPlaceholder =
                  streaming && i === messages.length - 1 && m.role === "assistant" && m.content === ""
                if (isStreamingPlaceholder) return null
                const bubble: ChatBubble = {
                  id:         m.id,
                  role:       m.role,
                  content:    m.content,
                  createdAt:  m.createdAt,
                  authorName: m.role === "assistant" ? "Forge" : m.role === "user" ? "You" : null,
                }
                return (
                  <ChatMessageBubble
                    key={m.id}
                    msg={bubble}
                    userLabel="You"
                    assistantLabel="Forge"
                  />
                )
              })}
              {streaming && <ChatThinkingIndicator label="Forge" />}
            </div>

            <footer className="border-t border-border p-3">
              <div className="flex gap-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      send()
                    }
                  }}
                  placeholder="Ask Forge…"
                  rows={2}
                  className="flex-1"
                  disabled={streaming}
                />
                <Button
                  type="button" variant="red" size="md"
                  onClick={send}
                  disabled={streaming || !draft.trim()}
                  className="self-end"
                >
                  <Send size={16} strokeWidth={2} />
                </Button>
              </div>
            </footer>
          </Card>

          {/* Right: pending proposals */}
          <Card className="flex w-full min-w-0 flex-col p-0 lg:w-[440px] lg:flex-none">
            <header className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold">
                Pending changes{proposals.length > 0 ? ` (${proposals.length})` : ""}
              </h3>
              <p className="mt-0.5 text-[10px] text-app-subtle">
                Apply each card individually. Applied cards stay visible so you can keep iterating.
              </p>
            </header>
            <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
              {proposals.length === 0 ? (
                <p className="text-xs text-app-muted">
                  AI-suggested changes land here. Ask for a concrete change in the chat to populate this list.
                </p>
              ) : (
                proposals.map((p) => (
                  <FieldUpdateCard
                    key={p.id}
                    proposal={p}
                    currentRolePrompt={currentRolePrompt}
                    applying={applyingId === p.id}
                    onApply={() => applyOne(p)}
                    onReject={() => rejectOne(p.id)}
                  />
                ))
              )}
              {applyError && (
                <p className="rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
                  {applyError}
                </p>
              )}
            </div>
          </Card>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// ────────────────────────── proposal card ──────────────────────────

type CardProps = {
  proposal: Proposal
  currentRolePrompt: string
  applying: boolean
  onApply: () => void
  onReject: () => void
}

function FieldUpdateCard({ proposal, currentRolePrompt, applying, onApply, onReject }: CardProps) {
  const field   = String(proposal.input.field ?? "")
  const summary = (proposal.input.summary as string | undefined) ?? ""
  const after   = String(proposal.input.after ?? "")
  // For role_prompt the snapshot is the prop; for other fields we
  // don't have the value here, so show whatever the proposal carried
  // as `before` (Forge usually echoes it).
  const before  =
    field === "role_prompt"
      ? currentRolePrompt
      : String((proposal.input.before as string | undefined) ?? "")

  const segments: DiffSegment[] = wordDiff(before, after)
  const isCreation = before.trim().length === 0

  return (
    <div
      className={cn(
        "rounded-control border px-3 py-2 text-xs transition",
        proposal.applied
          ? "border-status-success/40 bg-status-success/10"
          : "border-vip-gold/40 bg-vip-goldMute",
      )}
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {proposal.number !== undefined && proposal.number !== null && (
            <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-subtle">
              #{proposal.number}
            </span>
          )}
          <span className="font-medium text-app-text">Update field</span>
          {field && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">{field}</code>
          )}
          {proposal.applied && (
            <span className="inline-flex items-center gap-1 rounded-chip border border-status-success/40 bg-status-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-status-success">
              <Check size={10} strokeWidth={3} /> Applied
            </span>
          )}
        </div>
        {!proposal.applied && (
          <button
            type="button"
            onClick={onReject}
            className="text-app-subtle hover:text-app-text"
            aria-label="Reject proposal"
            disabled={applying}
          >
            <X size={12} strokeWidth={2} />
          </button>
        )}
      </header>

      {summary && <p className="mb-1.5 text-app-muted">{summary}</p>}

      <div className="text-[11px]">
        {isCreation && (
          <div className="mb-1 text-[10px] text-vip-goldSoft">new field value</div>
        )}
        <div className="max-h-56 overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-2 leading-relaxed text-app-text">
          {segments.length === 0 ? (
            <span className="text-app-muted">(no change)</span>
          ) : (
            segments.map((s, i) => <DiffChunk key={i} seg={s} />)
          )}
        </div>
      </div>

      {!proposal.applied && (
        <footer className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onReject} disabled={applying}>
            Reject
          </Button>
          <Button type="button" variant="red" size="sm" onClick={onApply} disabled={applying}>
            {applying ? "Applying…" : "Apply"}
          </Button>
        </footer>
      )}
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return (
      <span className="rounded bg-status-success/15 text-status-success">
        {seg.text}
      </span>
    )
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}
